import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Reveal from '@/components/Reveal';
import Img from '@/components/Img';
import Seo from '@/components/Seo';
import Faq from '@/components/Faq';
import GoogleReviews from '@/components/GoogleReviews';
import PromoModal from '@/components/PromoModal';
import { HOME_PAGE } from '@/constants/testIds';
import { BRAND, CONTACT } from '@/constants/site';
import { HOME_FAQS } from '@/constants/faqs';

const HERO_LEFT = '/assets/hero-fitting.jpg';
const HERO_RIGHT = '/assets/hero-tie.jpg';
const FOUNDER_ATELIER = '/assets/founder-gill-atelier.jpg';
const WOMENS_PREVIEW = '/assets/w-blue.jpg';
const MENS_PREVIEW = '/assets/m-suits-pair.jpg';
const CRAFTS_IMG = '/assets/m-detail.jpg';

export default function Home() {
  return (
    <div data-testid={HOME_PAGE.hero}>
      <PromoModal />
      <Seo
        title="Bombay Bespoke Fashion House — Bespoke Tailoring & Couture, Dubai"
        description="Three decades of bespoke craftsmanship in Al Karama, Dubai. Suits, sherwanis, lehengas, gowns and couture — by Mr. Hardeep Singh Gill & Mrs. Vandana. Book a private consultation."
        canonical="https://bombaybespokefashion.com/"
        ogImage="https://bombaybespokefashion.com/assets/image-1.jpg"
      />
      {/* HERO — asymmetric split with two images */}
      <section className="relative bg-[hsl(var(--bb-ivory))] overflow-hidden">
        <div className="bb-container pt-12 md:pt-20 pb-16 md:pb-28 grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <div className="bb-eyebrow">{BRAND.legalName} · Dubai</div>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="bb-display text-[56px] sm:text-[80px] lg:text-[112px] mt-5 text-[hsl(var(--bb-text))]">
                Thirty years
                <br />
                of <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">sartorial</span>
                <br />
                excellence.
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-8 max-w-xl text-base md:text-lg text-[hsl(var(--bb-text-muted))] leading-relaxed">
                A house of two — <em className="italic">VM by Vandana</em> for women’s
                couture, and <em className="italic">Bombay Bespoke</em> for the master
                tailored gentleman. Founded by Mr. Hardeep Singh Gill, in Dubai.
              </p>
            </Reveal>
            <Reveal delay={360}>
              <div className="mt-10 flex flex-wrap items-center gap-5">
                <Link
                  to="/book-appointment"
                  data-testid={HOME_PAGE.heroCta}
                  className="group inline-flex items-center gap-3 px-7 py-4 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.24em] text-[11px] font-medium hover:bg-[hsl(var(--bb-gold-deep))] transition-colors duration-500"
                >
                  Book an appointment
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/our-story"
                  className="bb-link bb-link-reverse text-[hsl(var(--bb-text))]"
                >
                  Our Story
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-5 order-1 lg:order-2 relative">
            <div className="grid grid-cols-5 gap-3 md:gap-4">
              <Reveal className="col-span-3 bb-image-hover relative aspect-[3/4] overflow-hidden bg-[hsl(var(--bb-charcoal-soft))]" delay={200}>
                <Img src={HERO_LEFT} alt="Bespoke suit fitting at Bombay Bespoke, Dubai" imgClassName="w-full h-full object-cover" priority />
              </Reveal>
              <Reveal className="col-span-2 self-end bb-image-hover relative aspect-[3/4] overflow-hidden bg-[hsl(var(--bb-ivory-soft))]" delay={420}>
                <Img src={HERO_RIGHT} alt="Master tailor styling a client with a bespoke tie" imgClassName="w-full h-full object-cover" />
                <span className="absolute left-0 bottom-0 right-0 px-3 py-2 bg-[hsl(var(--bb-text))]/85 backdrop-blur-sm text-[10px] tracking-[0.24em] uppercase text-[hsl(var(--bb-gold))]">
                  Master Tailored
                </span>
              </Reveal>
            </div>
            <div className="mt-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--bb-text-muted))]">
              <span className="h-px w-12 bg-[hsl(var(--bb-gold))]" />
              Dubai
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER — Mr. Gill at the atelier (below the fold) */}
      <section
        className="relative bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] py-20 md:py-32 overflow-hidden bb-grain"
        data-testid="home-founder"
      >
        <div className="relative z-10 bb-container grid grid-cols-12 gap-8 md:gap-14 items-center">
          <div className="col-span-12 lg:col-span-7">
            <Reveal className="bb-image-hover relative aspect-[16/9] lg:aspect-[16/10] overflow-hidden border border-[hsl(var(--bb-text))]/10">
              <Img
                src={FOUNDER_ATELIER}
                alt="Mr. Hardeep Singh Gill at work in the Bombay Bespoke atelier"
                imgClassName="w-full h-full object-cover"
              />
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pl-6">
            <Reveal>
              <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">The Master Tailor</div>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="bb-display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[0.98]">
                Mr. Hardeep <span className="bb-italic text-[hsl(var(--bb-gold))]">Singh Gill.</span>
              </h2>
            </Reveal>
            <Reveal delay={220}>
              <p className="mt-6 text-base md:text-lg text-[hsl(var(--bb-text))]/72 leading-relaxed">
                Three decades at the cutting table. Every pattern drafted by hand, every
                cloth chosen in conversation, every fitting personally attended. This is
                the house Mr. Gill built — where a suit is not sold, it is made for you.
              </p>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-8">
                <Link
                  to="/our-story"
                  className="bb-link bb-link-reverse text-[hsl(var(--bb-gold))]"
                >
                  Read our story →
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WOMEN'S DESIGN PREVIEW — ivory, emerald accent */}
      <section
        className="relative bg-[hsl(var(--bb-ivory-soft))] py-20 md:py-32 overflow-hidden"
        data-testid={HOME_PAGE.womensPreview}
      >
        <div className="bb-container grid grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="col-span-12 lg:col-span-6 lg:order-2">
            <Reveal className="bb-image-hover relative aspect-[4/5] overflow-hidden">
              <Img src={WOMENS_PREVIEW} alt="VM by Vandana" imgClassName="w-full h-full object-cover" />
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:order-1 lg:pr-10">
            <Reveal>
              <div className="bb-eyebrow-emerald">VM by Vandana</div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="bb-display text-5xl md:text-6xl lg:text-7xl mt-4 text-[hsl(var(--bb-text))]">
                Couture <span className="bb-italic text-[hsl(var(--bb-emerald))]">in the language</span>
                <br />
                of every woman.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 max-w-lg text-base md:text-lg text-[hsl(var(--bb-text-muted))] leading-relaxed">
                Lehengas, gowns, saris and custom couture — drawn by Mrs. Vandana, shaped
                around silhouette, season, and the woman who will wear it.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-9">
                <Link
                  to="/womens-design"
                  data-testid={HOME_PAGE.womensCta}
                  className="bb-link bb-link-reverse text-[hsl(var(--bb-emerald))]"
                >
                  Explore Women’s Design →
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MEN'S BESPOKE PREVIEW — dark charcoal, gold accent */}
      <section
        className="relative bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] py-20 md:py-32 overflow-hidden bb-grain"
        data-testid={HOME_PAGE.mensPreview}
      >
        <div className="relative z-10 bb-container grid grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="col-span-12 lg:col-span-6">
            <Reveal className="bb-image-hover relative aspect-[4/5] overflow-hidden border border-[hsl(var(--bb-text))]/10">
              <Img src={MENS_PREVIEW} alt="Bombay Bespoke menswear" imgClassName="w-full h-full object-cover" />
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:pl-8">
            <Reveal>
              <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">Bombay Bespoke</div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="bb-display text-5xl md:text-6xl lg:text-7xl mt-4">
                A house cut <span className="bb-italic text-[hsl(var(--bb-gold))]">to a man’s</span>
                <br />
                own measure.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="mt-6 max-w-lg text-base md:text-lg text-[hsl(var(--bb-text))]/70 leading-relaxed">
                Suits, shirts, kurtas, sherwanis. Drafted by Mr. Hardeep Singh Gill from a
                pattern made only for you, finished by hand in the atelier.
              </p>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-9">
                <Link
                  to="/mens-bespoke"
                  data-testid={HOME_PAGE.mensCta}
                  className="bb-link bb-link-reverse text-[hsl(var(--bb-gold))]"
                >
                  Explore Men’s Bespoke →
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CRAFTSMANSHIP TEASE — large editorial quote */}
      <section className="relative bg-[hsl(var(--bb-ivory))] py-24 md:py-36" data-testid={HOME_PAGE.craftsmanship}>
        <div className="bb-container grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <div className="bb-eyebrow">The Craft</div>
            </Reveal>
            <Reveal delay={120}>
              <p className="bb-display text-4xl md:text-5xl lg:text-6xl mt-6 text-[hsl(var(--bb-text))] leading-[1.05]">
                “True luxury is in the
                <span className="bb-italic text-[hsl(var(--bb-gold-deep))]"> details</span> —
                in the seam you cannot see, the buttonhole sewn by hand,
                the line that follows the body and not the trend.”
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-8 flex items-center gap-4 text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--bb-text-muted))]">
                <span className="h-px w-10 bg-[hsl(var(--bb-gold))]" />
                Mr. Hardeep Singh Gill — Founder
              </div>
            </Reveal>
            <Reveal delay={320}>
              <Link to="/our-story" className="mt-8 inline-block bb-link bb-link-reverse text-[hsl(var(--bb-text))]">
                Read our story →
              </Link>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal className="bb-image-hover relative aspect-[4/5] overflow-hidden">
              <Img src={CRAFTS_IMG} alt="Atelier fabrics" imgClassName="w-full h-full object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* VISIT + BOOK CTA */}
      <section
        className="relative bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] py-20 md:py-28 overflow-hidden bb-grain"
        data-testid={HOME_PAGE.visitCta}
      >
        <div className="relative z-10 bb-container grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 lg:col-span-7">
            <div className="bb-eyebrow text-[hsl(var(--bb-gold))]">Visit the Atelier</div>
            <h3 className="bb-display text-4xl md:text-6xl mt-4">
              By appointment.
              <br />
              <span className="bb-italic text-[hsl(var(--bb-gold))]">As it should be.</span>
            </h3>
            <p className="mt-6 max-w-xl text-[hsl(var(--bb-text))]/70 text-base leading-relaxed">
              A private consultation begins with a conversation. Tell us the occasion;
              we’ll prepare the cloth, the references, and the time.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/book-appointment"
                className="inline-flex items-center px-6 py-4 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.24em] text-[11px] font-semibold hover:bg-[hsl(var(--bb-text))] transition-colors duration-500"
              >
                Book an appointment
              </Link>
              <Link
                to="/contact"
                className="bb-link bb-link-reverse text-[hsl(var(--bb-text))]"
              >
                Contact the house →
              </Link>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pl-10">
            <div className="bb-image-hover relative aspect-[4/5] overflow-hidden border border-[hsl(var(--bb-text))]/15">
              <Img src="/assets/image-1.jpg" alt="Bombay Bespoke storefront, Dubai" imgClassName="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute left-5 bottom-5 right-5">
                <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--bb-gold))]">The Storefront</div>
                <div className="mt-2 bb-display text-2xl text-[hsl(var(--bb-text))]">
                  Find us in <span className="bb-italic text-[hsl(var(--bb-gold))]">Dubai.</span>
                </div>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-5 text-xs">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--bb-gold))]">Hours</div>
                <div className="mt-2 text-[hsl(var(--bb-text))]/80 leading-relaxed">
                  {CONTACT.hours}
                  <br />
                  {CONTACT.hoursSunday}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--bb-gold))]">Direct</div>
                <div className="mt-2 leading-relaxed">
                  <a href={`tel:${CONTACT.phoneRaw}`} className="block hover:text-[hsl(var(--bb-gold))]">{CONTACT.phone}</a>
                  <a href={`mailto:${CONTACT.email}`} className="block hover:text-[hsl(var(--bb-gold))] mt-1 break-all">{CONTACT.email}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <section className="bg-[hsl(var(--bb-ivory))] border-t border-[hsl(var(--bb-text))]/8">
        <GoogleReviews variant="home" />
      </section>

      {/* FAQ — general questions */}
      <section className="bg-[hsl(var(--bb-ivory-soft))] border-t border-[hsl(var(--bb-text))]/8">
        <Faq
          eyebrow="Common questions"
          heading="About the house,"
          italicWord="answered."
          items={HOME_FAQS}
          tone="gold"
        />
      </section>
    </div>
  );
}
